import { useCallback, useEffect, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import { useDebounceFn } from 'ahooks';
import {
    type SupportedCountry,
    FindPlaceApiProviderEnum,
    FindPlaceTypeEnum,
} from 'generated/graphql';
import {
    FIND_PLACE_BY_KEYWORD_ACTION,
    type loader,
} from '~/routes/actions.location.find-place-by-keyword';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';

interface UsePlaceFinderProps {
    type?: FindPlaceTypeEnum;
    country: SupportedCountry;
    apiProvider?: FindPlaceApiProviderEnum;
    /**
     * When provided, selects the API provider per search based on the keyword.
     * Useful for HDE where postcode searches should use ServiceableArea (multiple suburbs)
     * while address/suburb searches use Mapbox.
     */
    apiProviderSelector?: (keyword: string) => FindPlaceApiProviderEnum;
}

/**
 * Hook to search for a list of locations by keyword.
 * @todo Merge with `usePlaceFinder` in modules/location.
 */
export const usePlaceFinder = (props: UsePlaceFinderProps) => {
    const {
        type = FindPlaceTypeEnum.SuburbMixed,
        country,
        apiProvider = FindPlaceApiProviderEnum.ServiceableArea,
        apiProviderSelector,
    } = props;

    const fetcher = useFetcher<typeof loader>();
    const [places, setPlaces] = useState<DetailedPlace[]>([]);
    const searchCountry = country;

    const updateKeyword = useCallback(
        async (keyword: string, limit: number) => {
            if (!keyword || keyword.trim().length < 2) {
                return;
            }

            if (fetcher.state === 'idle') {
                const formData = new FormData();
                const provider =
                    apiProviderSelector?.(keyword.trim()) ?? apiProvider;

                formData.append('keyword', keyword.trim());
                formData.append('type', type.toString());
                formData.append('limit', limit.toString());
                formData.append('country', searchCountry.toString());
                formData.append('apiProvider', provider.toString());
                fetcher.submit(formData, {
                    method: 'get',
                    action: FIND_PLACE_BY_KEYWORD_ACTION,
                });
            }
        },
        [searchCountry, fetcher, type, apiProvider, apiProviderSelector],
    );

    const { run: handlePlaceFind } = useDebounceFn(updateKeyword, {
        wait: 500,
    });

    const resetPlaces = useCallback(() => {
        setPlaces([]);
    }, []);

    useEffect(() => {
        // TODO: do we need to check for duplicated places?
        setPlaces(fetcher?.data ?? []);
    }, [fetcher.data]);

    return {
        places,
        loading: fetcher.state === 'loading' || fetcher.state === 'submitting',
        handlePlaceFind,
        resetPlaces,
    };
};
